import { IDENTITY_MATRIX } from '../core/geometry';
import {
  DEFAULT_LIMITS,
  createDocument,
  createGroupLayer,
  createSolidBackgroundLayer,
  createId,
  createRasterLayer,
  validateDimensions,
  walkLayers,
  type BlendMode,
  type ImportNotice,
  type Layer as EditorLayer,
  type LayerMask,
} from '../core/model';
import { RasterTileStore } from '../core/tiles';
import { psdColorToHex, textApproximations, textLayerFromPsdMetadata, type PsdTextMetadata } from './psd-text';
import type { CodecContext, CodecReadResult, DocumentCodec } from './types';
import { throwIfAborted } from './types';

const PSD_SIGNATURE = 0x38425053;

function blendMode(mode: string | undefined): BlendMode | null {
  const normalized = mode?.replaceAll(' ', '-');
  const supported: BlendMode[] = ['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion'];
  return supported.find((candidate) => candidate === normalized) ?? null;
}

/** sRGB encoding of one linear-light channel value in 0..1. */
function encodeSrgb(value: number): number {
  return value <= 0.0031308 ? 12.92 * value : 1.055 * Math.pow(value, 1 / 2.4) - 0.055;
}

/**
 * 8-bit RGBA from PSD pixel data. Photoshop's 32-bit mode stores linear-light
 * floats (gamma 1.0); colour channels are sRGB-encoded so the result matches
 * what Photoshop displays, while alpha and mask coverage stay linear.
 */
function bytePixels(pixelData: { data: Uint8Array | Uint8ClampedArray | Uint16Array | Float32Array; width: number; height: number }, coverage = false): ImageData {
  if (pixelData.data instanceof Uint8ClampedArray) {
    return new ImageData(new Uint8ClampedArray(pixelData.data), pixelData.width, pixelData.height);
  }
  const converted = new Uint8ClampedArray(pixelData.data.length);
  if (pixelData.data instanceof Uint16Array) {
    for (let index = 0; index < converted.length; index += 1) converted[index] = Math.round((pixelData.data[index] ?? 0) / 257);
  } else if (pixelData.data instanceof Float32Array) {
    for (let index = 0; index < converted.length; index += 1) {
      const value = Math.max(0, Math.min(1, pixelData.data[index] ?? 0));
      converted[index] = Math.round((coverage || index % 4 === 3 ? value : encodeSrgb(value)) * 255);
    }
  } else {
    converted.set(pixelData.data);
  }
  return new ImageData(converted, pixelData.width, pixelData.height);
}

type PsdLayer = import('ag-psd').Layer;
type PsdMask = import('ag-psd').LayerMaskData;

function safeMetadata(layer: PsdLayer): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};
  if (layer.text) {
    // Base style properties may live only in per-character style runs
    // (Photopea often writes them there); fall back to the first run.
    const style = layer.text.style;
    const run = layer.text.styleRuns?.[0]?.style;
    metadata.text = {
      text: layer.text.text,
      transform: layer.text.transform,
      antiAlias: layer.text.antiAlias,
      ...(style || run ? {
        style: {
          font: style?.font?.name ?? run?.font?.name,
          fontSize: style?.fontSize ?? run?.fontSize,
          tracking: style?.tracking ?? run?.tracking,
          leading: style?.leading ?? run?.leading,
          autoLeading: style?.autoLeading ?? run?.autoLeading,
          fillColor: style?.fillColor ?? run?.fillColor,
          fauxBold: style?.fauxBold ?? run?.fauxBold,
        },
      } : {}),
      ...(layer.text.paragraphStyle?.justification ? { justification: layer.text.paragraphStyle.justification } : {}),
      ...(typeof layer.text.paragraphStyle?.autoLeading === 'number' ? { autoLeadingRatio: layer.text.paragraphStyle.autoLeading } : {}),
      ...(layer.text.shapeType ? { shapeType: layer.text.shapeType } : {}),
      ...(layer.text.boxBounds ? { boxBounds: layer.text.boxBounds } : {}),
    };
  }
  if (layer.vectorMask) metadata.vectorMask = { pathCount: layer.vectorMask.paths.length, invert: layer.vectorMask.invert ?? false };
  if (layer.vectorFill) metadata.vectorFillType = layer.vectorFill.type;
  if (layer.adjustment) metadata.adjustmentType = layer.adjustment.type;
  if (layer.placedLayer) metadata.placedLayer = { id: layer.placedLayer.id, type: layer.placedLayer.type, width: layer.placedLayer.width, height: layer.placedLayer.height };
  if (layer.effects) metadata.effects = Object.keys(layer.effects);
  if (layer.artboard) metadata.artboard = { rect: layer.artboard.rect, backgroundType: layer.artboard.backgroundType };
  if (layer.id !== undefined) metadata.originalLayerId = layer.id;
  return metadata;
}

/** Where a layer's local (0, 0) sits in document space, and how large its mask may be. */
interface MaskFrame { originX: number; originY: number; width: number; height: number }

/**
 * Every decoded PSD mask of a layer, multiplied into one editable mask.
 *
 * Photoshop stores a vector mask rasterised in the user-mask channel and, when
 * the layer also has a pixel mask, that pixel mask as the "real" user mask.
 * Keeping only the first would silently discard the pixel mask; the rendered
 * result is the product of both, which a single Maipo mask represents exactly.
 */
function importMask(layer: PsdLayer, frame: MaskFrame, name: string, notices: ImportNotice[]): LayerMask | undefined {
  const sources = [layer.mask, layer.realMask].filter((mask): mask is PsdMask => Boolean(mask));
  if (sources.length === 0) return undefined;
  const decoded = sources.map((source) => ({
    image: source.imageData && source.imageData.width > 0 && source.imageData.height > 0 ? bytePixels(source.imageData, true) : null,
    left: (source.left ?? 0) - frame.originX,
    top: (source.top ?? 0) - frame.originY,
    fallback: (source.defaultColor ?? 0) >= 128 ? 255 : 0,
  }));
  const defaultAlpha: 0 | 255 = decoded.every((entry) => entry.fallback === 255) ? 255 : 0;
  // A mask that reveals everything changes nothing; fill layers carry these.
  if (defaultAlpha === 255 && decoded.every((entry) => !entry.image)) return undefined;
  if (sources.some((source) => (source.userMaskDensity ?? 1) < 1 || (source.vectorMaskDensity ?? 1) < 1 || (source.userMaskFeather ?? 0) > 0 || (source.vectorMaskFeather ?? 0) > 0)) {
    notices.push({ severity: 'warning', feature: 'layer-mask', layerName: name, message: 'Mask density or feather is not applied; the mask is used at full density without feathering.', handling: 'approximated' });
  }
  const width = Math.max(1, Math.ceil(frame.width));
  const height = Math.max(1, Math.ceil(frame.height));
  const store = new RasterTileStore(width, height);
  const images = decoded.filter((entry) => entry.image);
  const x0 = Math.max(0, Math.floor(Math.min(...images.map((entry) => entry.left), Infinity)));
  const y0 = Math.max(0, Math.floor(Math.min(...images.map((entry) => entry.top), Infinity)));
  const x1 = Math.min(width, Math.ceil(Math.max(...images.map((entry) => entry.left + entry.image!.width), -Infinity)));
  const y1 = Math.min(height, Math.ceil(Math.max(...images.map((entry) => entry.top + entry.image!.height), -Infinity)));
  const initialized = new Set<string>();
  for (let targetY = y0; targetY < y1; targetY += 1) {
    for (let targetX = x0; targetX < x1; targetX += 1) {
      let value = 255;
      for (const entry of decoded) {
        const x = targetX - entry.left;
        const y = targetY - entry.top;
        const inside = entry.image && x >= 0 && y >= 0 && x < entry.image.width && y < entry.image.height;
        value = value * (inside ? entry.image!.data[(y * entry.image!.width + x) * 4] ?? 0 : entry.fallback) / 255;
      }
      const tileX = Math.floor(targetX / store.tileSize);
      const tileY = Math.floor(targetY / store.tileSize);
      const key = RasterTileStore.key(tileX, tileY);
      const tile = store.getTile(tileX, tileY, true);
      if (!tile) continue;
      const dimensions = store.tileDimensions(tileX, tileY);
      if (!initialized.has(key)) {
        for (let index = 0; index < tile.length; index += 4) {
          tile[index] = 255;
          tile[index + 1] = 255;
          tile[index + 2] = 255;
          tile[index + 3] = defaultAlpha;
        }
        initialized.add(key);
      }
      tile[((targetY % store.tileSize) * dimensions.width + (targetX % store.tileSize)) * 4 + 3] = Math.round(value);
    }
  }
  if (sources.length > 1) {
    notices.push({ severity: 'info', feature: 'layer-mask', layerName: name, message: 'The vector mask and the pixel mask were combined into one editable pixel mask.', handling: 'rasterized' });
  }
  return {
    id: createId('mask'),
    enabled: !sources[0]!.disabled,
    linked: !sources[0]!.positionRelativeToLayer,
    defaultAlpha,
    tiles: store,
  };
}

function flattenConverted(converted: (EditorLayer | EditorLayer[] | null)[]): EditorLayer[] {
  return converted.flatMap((entry) => entry ? (Array.isArray(entry) ? entry : [entry]) : []);
}

const MAX_PSD_DEPTH = 64;

const EFFECT_LABELS: ReadonlyArray<readonly [keyof import('ag-psd').LayerEffectsInfo, string]> = [
  ['dropShadow', 'drop shadow'], ['innerShadow', 'inner shadow'], ['outerGlow', 'outer glow'], ['innerGlow', 'inner glow'],
  ['bevel', 'bevel and emboss'], ['satin', 'satin'], ['solidFill', 'color overlay'], ['gradientOverlay', 'gradient overlay'],
  ['patternOverlay', 'pattern overlay'], ['stroke', 'stroke'],
];

/** Effects Photoshop actually draws: the master switch and each effect's own switch. */
function enabledEffects(layer: PsdLayer): string[] {
  const effects = layer.effects;
  if (!effects || effects.disabled) return [];
  return EFFECT_LABELS.filter(([key]) => {
    const value = effects[key] as { enabled?: boolean } | { enabled?: boolean }[] | undefined;
    return (Array.isArray(value) ? value : value ? [value] : []).some((effect) => effect.enabled !== false);
  }).map(([, label]) => label);
}

interface ConversionContext {
  readonly notices: ImportNotice[];
  readonly width: number;
  readonly height: number;
}

/** Visibility, opacity, fill opacity, clipping, blend mode and effect notices shared by every layer kind. */
function applyCommonProperties(target: EditorLayer, layer: PsdLayer, name: string, context: ConversionContext): void {
  target.visible = !layer.hidden;
  target.opacity = layer.opacity ?? 1;
  target.clipping = layer.clipping ?? false;
  // Photoshop's Fill opacity fades layer content but not its effects; Maipo
  // renders fill opacity on content the same way. Photoshop offers no Fill for
  // groups, so a group's stored value is not carried.
  if (target.kind !== 'group') target.fillOpacity = layer.fillOpacity ?? 1;
  const mapped = blendMode(layer.blendMode);
  if (mapped) target.blendMode = mapped;
  else if (layer.blendMode && layer.blendMode !== 'pass through') {
    context.notices.push({ severity: 'warning', feature: 'blend-mode', layerName: name, message: `Blend mode “${layer.blendMode}” is rendered as normal.`, handling: 'approximated' });
  }
  const effects = enabledEffects(layer);
  if (effects.length) {
    context.notices.push({ severity: 'warning', feature: 'layer-effects', layerName: name, message: `Layer effects (${effects.join(', ')}) are not rendered: PSD layer pixels exclude effects. Their settings are retained.`, handling: 'omitted' });
  }
  target.extensions.psd = safeMetadata(layer);
}

/**
 * Whether a pass-through group holds a blend mode Maipo renders, whose result
 * in Photoshop would reach layers below the group. (Unsupported modes and PSD
 * adjustments already carry their own notices.)
 */
function passThroughReachesBelow(layer: PsdLayer): boolean {
  return (layer.children ?? []).some((child) => {
    if (child.children && (child.blendMode === 'pass through' || !child.blendMode)) return passThroughReachesBelow(child);
    const mapped = blendMode(child.blendMode);
    return mapped !== null && mapped !== 'normal';
  });
}

/** A mask revealing exactly one document rectangle, as an artboard clips its contents. */
function rectangleMask(frame: MaskFrame, rect: { left: number; top: number; right: number; bottom: number }): LayerMask {
  const store = new RasterTileStore(Math.max(1, frame.width), Math.max(1, frame.height));
  const x0 = Math.max(0, rect.left - frame.originX);
  const y0 = Math.max(0, rect.top - frame.originY);
  const x1 = Math.min(store.width, rect.right - frame.originX);
  const y1 = Math.min(store.height, rect.bottom - frame.originY);
  for (let tileY = Math.floor(y0 / store.tileSize); tileY * store.tileSize < y1; tileY += 1) {
    for (let tileX = Math.floor(x0 / store.tileSize); tileX * store.tileSize < x1; tileX += 1) {
      const tile = store.getTile(tileX, tileY, true);
      if (!tile) continue;
      const dimensions = store.tileDimensions(tileX, tileY);
      for (let y = 0; y < dimensions.height; y += 1) {
        for (let x = 0; x < dimensions.width; x += 1) {
          const documentX = tileX * store.tileSize + x;
          const documentY = tileY * store.tileSize + y;
          const offset = (y * dimensions.width + x) * 4;
          tile[offset] = 255; tile[offset + 1] = 255; tile[offset + 2] = 255;
          tile[offset + 3] = documentX >= x0 && documentX < x1 && documentY >= y0 && documentY < y1 ? 255 : 0;
        }
      }
    }
  }
  return { id: createId('mask'), enabled: true, linked: true, defaultAlpha: 0, tiles: store };
}

function artboardBackground(layer: PsdLayer): string | null {
  const artboard = layer.artboard;
  if (!artboard) return null;
  // backgroundType: 1 white, 2 black, 3 transparent, 4 custom colour.
  if (artboard.backgroundType === 1) return '#ffffff';
  if (artboard.backgroundType === 2) return '#000000';
  if (artboard.backgroundType === 4) return psdColorToHex(artboard.color);
  return null;
}

function convertLayer(layer: PsdLayer, context: ConversionContext, depth = 0): EditorLayer | EditorLayer[] | null {
  const { notices } = context;
  const name = layer.name?.trim() || 'Untitled layer';
  if (layer.children) {
    // Guard synchronous recursion before a crafted deeply-nested PSD overflows
    // the stack — surfaces a friendly error instead of a RangeError.
    if (depth >= MAX_PSD_DEPTH) throw new Error(`PSD group nesting exceeds the ${MAX_PSD_DEPTH}-level safety limit.`);
    // PSD stores children bottom-first; Maipo's children[0] is the topmost layer.
    const children = flattenConverted(layer.children.map((child) => convertLayer(child, context, depth + 1))).reverse();
    const background = artboardBackground(layer);
    const rect = layer.artboard?.rect;
    if (background && rect && rect.right > rect.left && rect.bottom > rect.top) {
      // The artboard's own background colour is part of its rendering.
      const fill = createSolidBackgroundLayer(background, rect.right - rect.left, rect.bottom - rect.top);
      fill.name = 'Artboard background';
      fill.transform = [1, 0, 0, 1, rect.left, rect.top];
      children.push(fill);
    }
    const group = createGroupLayer(name, children);
    applyCommonProperties(group, layer, name, context);
    group.expanded = layer.opened ?? true;
    if ((layer.blendMode === 'pass through' || !layer.blendMode) && passThroughReachesBelow(layer)) {
      notices.push({ severity: 'warning', feature: 'blend-mode', layerName: name, message: 'This pass-through group is composited in isolation: blend modes and adjustments inside it no longer affect layers below the group.', handling: 'approximated' });
    }
    const documentFrame = { originX: 0, originY: 0, width: context.width, height: context.height };
    const mask = importMask(layer, documentFrame, name, notices);
    if (mask) group.mask = mask;
    else if (rect && rect.right > rect.left && rect.bottom > rect.top) {
      // An artboard clips its contents to its rectangle; an exact group mask
      // keeps content outside the board hidden, as Photoshop renders it.
      group.mask = rectangleMask(documentFrame, rect);
    }
    return group;
  }
  if (layer.text?.text) {
    // Intended editing model: PSD text arrives EDITABLE. The retained metadata
    // rebuilds a native text layer; the baked preview (when present) is kept
    // as a hidden sibling directly beneath it as the exact-appearance fallback.
    const metadata = safeMetadata(layer);
    const editable = textLayerFromPsdMetadata(metadata.text as PsdTextMetadata, {
      name,
      ...(typeof layer.left === 'number' && typeof layer.right === 'number' && layer.right > layer.left
        ? { pixelBounds: { left: layer.left, right: layer.right } }
        : {}),
    });
    applyCommonProperties(editable, layer, name, context);
    editable.extensions.psd = metadata;
    // A warning, not info: the rebuilt layout can visibly differ, and the
    // report headline must not claim every property was preserved.
    notices.push({ severity: 'warning', feature: 'editable-text', layerName: name, message: 'Text was rebuilt as an editable layer from retained metadata; layout is approximate and rendering depends on font availability. The original preview is kept as a hidden layer.', handling: 'approximated' });
    for (const loss of textApproximations(layer.text)) notices.push({ severity: 'warning', feature: 'editable-text', layerName: name, message: loss, handling: 'approximated' });
    if (layer.mask || layer.realMask) {
      const [tx, ty] = [editable.transform[4], editable.transform[5]];
      const right = Math.max(...[layer.mask, layer.realMask].map((mask) => mask?.right ?? 0));
      const bottom = Math.max(...[layer.mask, layer.realMask].map((mask) => mask?.bottom ?? 0));
      const mask = importMask(layer, { originX: tx, originY: ty, width: Math.max(editable.text.box.width, right - tx), height: Math.max(editable.text.box.height, bottom - ty) }, name, notices);
      if (mask) editable.mask = mask;
    }
    const output: EditorLayer[] = [];
    if (layer.imageData) {
      const previewImage = bytePixels(layer.imageData);
      if (previewImage.width >= 1 && previewImage.height >= 1) {
        const preview = createRasterLayer(`${name} (PSD preview)`, previewImage.width, previewImage.height);
        preview.raster = RasterTileStore.fromImageData(previewImage);
        preview.visible = false;
        preview.transform = [1, 0, 0, 1, layer.left ?? 0, layer.top ?? 0];
        output.push(preview);
      }
    }
    // Emitted bottom-first like PSD children; the caller's reverse puts the
    // editable layer above its hidden preview.
    output.push(editable);
    return output;
  }
  if (!layer.imageData) {
    const message = layer.adjustment
      ? `PSD ${layer.adjustment.type} adjustment is not applied; its parameters are retained.`
      : layer.vectorFill || layer.vectorMask
        ? 'This shape or fill layer has no decoded pixels, so it is not rendered; its vector data is retained.'
        : 'No layer pixel preview was decoded; the layer was retained as opaque metadata but is not visible.';
    notices.push({ severity: 'warning', feature: layer.adjustment ? 'adjustment-layer' : 'layer-pixels', layerName: name, message, handling: 'opaque-metadata' });
    const width = Math.max(1, (layer.right ?? 1) - (layer.left ?? 0));
    const height = Math.max(1, (layer.bottom ?? 1) - (layer.top ?? 0));
    const placeholder = createRasterLayer(name, width, height);
    placeholder.visible = !layer.hidden;
    placeholder.opacity = layer.opacity ?? 1;
    placeholder.fillOpacity = layer.fillOpacity ?? 1;
    placeholder.clipping = layer.clipping ?? false;
    placeholder.transform = [1, 0, 0, 1, layer.left ?? 0, layer.top ?? 0];
    placeholder.extensions.psd = safeMetadata(layer);
    return placeholder;
  }
  const image = bytePixels(layer.imageData);
  if (image.width < 1 || image.height < 1) return null;
  const raster = createRasterLayer(name, image.width, image.height);
  raster.raster = RasterTileStore.fromImageData(image);
  raster.transform = [1, 0, 0, 1, layer.left ?? 0, layer.top ?? 0];
  applyCommonProperties(raster, layer, name, context);
  const mask = importMask(layer, { originX: layer.left ?? 0, originY: layer.top ?? 0, width: image.width, height: image.height }, name, notices);
  if (mask) raster.mask = mask;
  if (layer.vectorMask || layer.vectorFill || layer.vectorStroke) notices.push({ severity: 'warning', feature: 'vector-shape', layerName: name, message: 'Vector metadata is summarized; the decoded raster preview is used.', handling: 'rasterized' });
  if (layer.adjustment) notices.push({ severity: 'warning', feature: 'adjustment-layer', layerName: name, message: `PSD ${layer.adjustment.type} parameters are retained; the decoded preview is used.`, handling: 'rasterized' });
  if (layer.placedLayer) notices.push({ severity: 'warning', feature: 'smart-object', layerName: name, message: 'Placed-layer identity is retained; linked editing is unavailable.', handling: 'rasterized' });
  return raster;
}

function readHeader(buffer: ArrayBuffer): { width: number; height: number; depth: number; colorMode: number } {
  if (buffer.byteLength < 26) throw new Error('PSD header is truncated.');
  const view = new DataView(buffer);
  if (view.getUint32(0, false) !== PSD_SIGNATURE) throw new Error('The file is not a PSD document.');
  if (view.getUint16(4, false) !== 1) throw new Error('PSB/large-document files are not supported.');
  return { height: view.getUint32(14, false), width: view.getUint32(18, false), depth: view.getUint16(22, false), colorMode: view.getUint16(24, false) };
}

export class PsdCodec implements DocumentCodec {
  readonly id = 'psd';
  readonly label = 'Adobe Photoshop document import';

  canRead(file: File): boolean {
    return file.type === 'image/vnd.adobe.photoshop' || /\.psd$/i.test(file.name);
  }

  async read(file: File, context: CodecContext = {}): Promise<CodecReadResult> {
    context.onProgress?.({ phase: 'validating', progress: 0.03, message: 'Checking PSD header and limits' });
    if (file.size > 512 * 1024 * 1024) throw new Error('PSD exceeds the 512 MiB compressed-file safety limit.');
    const buffer = await file.arrayBuffer();
    throwIfAborted(context.signal);
    const header = readHeader(buffer);
    validateDimensions(header.width, header.height);
    context.onProgress?.({ phase: 'decoding', progress: 0.15, message: 'Loading the PSD decoder' });
    const { readPsd } = await import('ag-psd');
    throwIfAborted(context.signal);
    const psd = readPsd(buffer, {
      useImageData: true,
      skipThumbnail: false,
      skipLinkedFilesData: true,
      totalMemoryLimit: DEFAULT_LIMITS.maxDecodedBytes,
      logMissingFeatures: false,
    });
    throwIfAborted(context.signal);
    const notices: ImportNotice[] = [];
    if (header.depth === 32) notices.push({ severity: 'warning', feature: 'bit-depth', message: '32-bit linear-light channels were converted to 8-bit sRGB; values above 1.0 (HDR highlights) are clipped.', handling: 'approximated' });
    else if (header.depth !== 8) notices.push({ severity: 'warning', feature: 'bit-depth', message: `${header.depth}-bit channels were converted to Maipo’s 8-bit working format.`, handling: 'approximated' });
    if (header.colorMode !== 3) notices.push({ severity: 'warning', feature: 'color-mode', message: 'The source color mode was converted to the sRGB working space by the PSD decoder.', handling: 'approximated' });
    context.onProgress?.({ phase: 'converting', progress: 0.65, message: 'Converting PSD layers to tiles' });
    // PSD stores children bottom-first; Maipo's children[0] is the topmost layer.
    const conversion: ConversionContext = { notices, width: psd.width, height: psd.height };
    let children = flattenConverted((psd.children ?? []).map((layer) => convertLayer(layer, conversion))).reverse();
    if (children.length === 0 && psd.imageData) {
      const image = bytePixels(psd.imageData);
      const fallback = createRasterLayer('Flattened PSD preview', image.width, image.height);
      fallback.raster = RasterTileStore.fromImageData(image);
      fallback.transform = IDENTITY_MATRIX;
      children = [fallback];
      notices.push({ severity: 'warning', feature: 'layer-structure', message: 'No readable layer pixels were available. A flattened document preview was used.', handling: 'rasterized' });
    }
    if (children.length === 0) throw new Error('The PSD contains no decodable layer or composite pixels.');
    const document = createDocument({ name: file.name.replace(/\.psd$/i, ''), width: psd.width, height: psd.height, background: 'transparent' });
    document.root.children = children;
    document.activeLayerIds = children[0] ? [children[0].id] : [];
    document.source = { kind: 'psd', name: file.name };
    document.extensions.psd = {
      bitsPerChannel: psd.bitsPerChannel,
      colorMode: psd.colorMode,
      hasLinkedFiles: Boolean(psd.linkedFiles?.length),
      hasAnnotations: Boolean(psd.annotations?.length),
    };
    if (psd.linkedFiles?.length) notices.push({ severity: 'warning', feature: 'linked-assets', message: 'Linked-file descriptors were detected, but their payloads were not loaded for security and memory safety.', handling: 'opaque-metadata' });
    if (walkLayers(document.root).length > DEFAULT_LIMITS.maxLayers) throw new Error(`PSD exceeds the ${DEFAULT_LIMITS.maxLayers.toLocaleString()} layer limit.`);
    const report = { codec: 'ag-psd 31.0.2', sourceName: file.name, notices };
    document.importReport = report;
    context.onProgress?.({ phase: 'converting', progress: 1, message: 'PSD ready' });
    return { document, report };
  }
}
